import React from 'react';
import { useTranslation } from 'react-i18next';

import { RfidToken } from '../../../interface/rfid-management.interface';
import { useAppDispatch } from '../../../redux/hooks';
import { deactivateTokenApi } from '../../../redux/slices/RfidTokenSlice';
import DisableSwitchWarning from '../../../shared-components/disable-warning/DisableWarning';

interface DeactivateCardModalProperties {
  token: RfidToken;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * Confirmation modal for terminal deactivation of an RFID card
 * @param {DeactivateCardModalProperties} properties - Component properties
 * @returns {React.JSX.Element} Deactivation warning modal
 */
function DeactivateCardModal(properties: DeactivateCardModalProperties): React.JSX.Element {
  const { token, onClose, onSuccess } = properties;
  const { t, i18n } = useTranslation(['devices', 'sharedComponent']);
  const dispatch = useAppDispatch();
  const [reason, setReason] = React.useState<string>(token?.reason || '');

  /**
   * Confirms and performs terminal deactivation
   * @returns {Promise<void>} Resolves when deactivation completes
   */
  const handleConfirmDeactivate = async (): Promise<void> => {
    try {
      const targetId = token.id || token._id || token.uid || '';
      await dispatch(
        deactivateTokenApi({
          tokenId: targetId,
          status: token.status,
          reason: reason || undefined,
        }),
      ).unwrap();
      if (onSuccess) {
        onSuccess();
      } else {
        onClose();
      }
    } catch {
      // Toast notification is handled by the thunk
    }
  };

  return (
    <div className="rfid-deactivate-modal-wrap" dir={i18n.dir()}>
      <DisableSwitchWarning
        message={
          <>
            <strong>
              {token.status === 'ACTIVE'
                ? t('deactivateTitle', {
                    uid: token.uid,
                    defaultValue: 'Permanently Deactivate Card {{uid}}?',
                  })
                : t('activateTitle', {
                    uid: token.uid,
                    defaultValue: 'Permanently Activate Card {{uid}}?',
                  })}
            </strong>
            <br />
            {token.status === 'ACTIVE'
              ? t('deactivateWarningDescription', {
                  defaultValue:
                    'Card deactivation is terminal with no reactivation allowed. Once deactivated, this physical RFID card will be permanently blocked from initiating charging sessions.',
                })
              : t('activateWarningDescription', {
                  defaultValue:
                    'Card activation will allow this physical RFID card to initiate charging sessions.',
                })}
            <br /><br />
            <div className="rfid-reason-text">
              <textarea
                className="reject-reason-input"
                placeholder={t('reason')}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            </div>
          </>
        }
        cancelLabel={t('cancel', { defaultValue: 'Cancel' })}
        confirmLabel={
          token.status === 'ACTIVE'
            ? t('deactivate', { defaultValue: 'Deactivate' })
            : t('activate', { defaultValue: 'Activate' })
        }
        onCancel={onClose}
        onConfirm={handleConfirmDeactivate}
      />
    </div>
  );
}

export default DeactivateCardModal;
